import { z } from 'zod';

const TREND = z.enum(['improving', 'stable', 'declining', 'unknown']);
const CONVERSATION_IDS = z.array(z.string());

const openIssueSchema = z.object({
  friendlyId: z.string(),
  subject: z.string(),
  status: z.string(),
  urgency: z.string(),
  nextAction: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? null),
});

const recentResolutionSchema = z.object({
  friendlyId: z.string(),
  issue: z.string(),
});

const recurringIssueSchema = z.object({
  suggestedAction: z.string().nullable(),
});

const supportHistorySchema = z
  .object({
    openIssues: z
      .array(openIssueSchema)
      .optional()
      .transform((value) => value ?? []),
    recentResolutions: z
      .array(recentResolutionSchema)
      .optional()
      .transform((value) => value ?? []),
    recurringIssues: z.array(recurringIssueSchema).nullable().optional(),
  })
  .transform((history) => ({
    openIssues: history.openIssues,
    recentResolutions: history.recentResolutions,
    recurringIssues: history.recurringIssues ?? null,
  }));

const siteHealthFactorsSchema = z.object({
  systemStability: z.number().int(),
  resolutionEfficiency: z.number().int(),
  clientEngagement: z.number().int(),
  supportBurden: z.number().int(),
});

const storedSiteHealthSchema = z.object({
  assessmentStatus: z.enum(['assessed', 'not_assessed']).optional(),
  overallScore: z.number().int().nullable().optional(),
  overallScoreReasoning: z.string().optional(),
  trend: TREND.optional(),
  reasoning: z.string().optional(),
  riskLevel: z.enum(['low', 'medium', 'high', 'critical']).nullable().optional(),
  riskLevelReasoning: z.string().optional(),
  factors: siteHealthFactorsSchema.nullable().optional(),
  attentionItems: z.array(z.string()).nullable().optional(),
  conversationIds: CONVERSATION_IDS.optional(),
});

const siteHealthSchema = storedSiteHealthSchema.transform((health) => {
  const overallScore = health.overallScore ?? null;
  const factors = health.factors ?? null;
  const hasScore = overallScore !== null;
  const assessmentStatus =
    health.assessmentStatus ?? (hasScore || factors !== null ? 'assessed' : 'not_assessed');

  return {
    assessmentStatus,
    overallScore,
    overallScoreReasoning: health.overallScoreReasoning ?? '',
    trend: health.trend ?? 'unknown',
    reasoning: health.reasoning ?? '',
    riskLevel: health.riskLevel ?? null,
    riskLevelReasoning: health.riskLevelReasoning ?? '',
    factors,
    attentionItems: health.attentionItems ?? null,
    conversationIds: health.conversationIds ?? [],
  };
});

const sentimentDriverSchema = z.union([
  z.string().transform((driver) => ({ driver, conversationIds: [] as string[] })),
  z.object({
    driver: z.string(),
    conversationIds: CONVERSATION_IDS.optional().transform((value) => value ?? []),
  }),
]);

export const clientSentimentOverall = [
  'very_positive',
  'positive',
  'neutral',
  'negative',
  'very_negative',
  'unknown',
] as const;

const storedClientSentimentSchema = z.object({
  assessmentStatus: z.enum(['assessed', 'unknown']).optional(),
  overall: z.enum(clientSentimentOverall),
  overallReasoning: z.string().optional(),
  lastKnownMood: z.string().nullable().optional(),
  sentimentDrivers: z
    .object({
      positive: z.array(sentimentDriverSchema).nullable().optional(),
      negative: z.array(sentimentDriverSchema).nullable().optional(),
    })
    .optional(),
  conversationIds: CONVERSATION_IDS.optional(),
});

const clientSentimentSchema = storedClientSentimentSchema.transform((sentiment) => {
  const overall = sentiment.overall;
  const assessmentStatus =
    sentiment.assessmentStatus ?? (overall === 'unknown' ? 'unknown' : 'assessed');

  return {
    assessmentStatus,
    overall,
    overallReasoning: sentiment.overallReasoning ?? '',
    lastKnownMood: sentiment.lastKnownMood ?? null,
    sentimentDrivers: {
      positive: sentiment.sentimentDrivers?.positive ?? null,
      negative: sentiment.sentimentDrivers?.negative ?? null,
    },
    conversationIds: sentiment.conversationIds ?? [],
  };
});

export const siteOverviewSchema = z.object({
  siteHealth: siteHealthSchema,
  clientSentiment: clientSentimentSchema,
  supportHistory: supportHistorySchema.optional().transform(
    (history) =>
      history ?? {
        openIssues: [],
        recentResolutions: [],
        recurringIssues: null,
      },
  ),
});

export type OpenIssue = z.infer<typeof openIssueSchema>;
export type RecurringIssue = z.infer<typeof recurringIssueSchema>;
export type SiteHealthFactors = z.infer<typeof siteHealthFactorsSchema>;
export type SiteOverviewSummary = z.infer<typeof siteOverviewSchema>;

export function parseSiteOverview(
  raw: string | Record<string, unknown> | null | undefined,
): SiteOverviewSummary | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  try {
    const value: unknown = typeof raw === 'string' ? (JSON.parse(raw) as unknown) : raw;
    const result = siteOverviewSchema.safeParse(value);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
